import {Modal} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils';
import {useState} from 'react';
import {FaCircleUser} from 'react-icons/fa6';
import {COLOR_BLACK_2} from '../../../../../config/constants/colorCodes';
import type {CommunityTopic} from '../../../../../core/entities/community/schema';
import {usePostAlertCommunityTopic} from '../../../../../core/repositories/communityRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../../../shared/components/SnackBar';
import {formatDateFullWithTime} from '../../../../../shared/services/formatDate';
import {LastConfirmModal} from './LastConfirmModal';

type Props = {
  opened: boolean;
  open: () => void;
  onClose: () => void;
  topic: CommunityTopic;
};

type CastedError = {message: AppError};

export const ReportTopicModal: React.FC<Props> = ({
  opened,
  open,
  onClose,
  topic,
}) => {
  const [
    isLastConfirmModalOpened,
    {open: openLastConfirmModal, close: closeLastConfirmModal},
  ] = useDisclosure(false);
  const [isReportLoading, setIsReportLoading] = useState(false);

  const reportTopic = usePostAlertCommunityTopic({
    topicId: topic.id,
    onMutateFnc() {
      setIsReportLoading(true);
    },
    onSuccessFnc() {
      showSuccessSnackBar({title: '送信しました', message: 'done'});
      closeLastConfirmModal();
    },
    onErrorFnc(error: unknown) {
      const castedError = error as CastedError;
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
    onSettledFnc() {
      setIsReportLoading(false);
    },
  });

  const onSubmit = () => {
    onClose();
    openLastConfirmModal();
  };

  const onLastConfirmSubmit = async () => {
    reportTopic.mutate();
  };

  const onLastConfirmClose = () => {
    closeLastConfirmModal();
    open();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        withCloseButton={false}
        size={668}
        padding={0}
        radius={12}
        centered={true}
      >
        <div className="p-24">
          <p className="text-md text-black-1 font-bold leading">
            不適切な内容の通報
          </p>
          <p className="text-base text-black-1 leading mt-16">
            トピックNo.{topic.id}を不適切な内容として通報しますか？
          </p>
          <div className="bg-background-1 p-16 rounded-4 mt-8">
            <div className="flex justify-start items-center">
              <FaCircleUser size={16} fill={COLOR_BLACK_2} />
              <p className="text-sm text-black-2 font-bold leading ml-4">
                {topic.nickname}
              </p>
              <p className="text-sm text-black-2 leading ml-8">
                投稿日: {formatDateFullWithTime(topic.createdAt)}
              </p>
            </div>
            <p className="text-md text-black-2 font-bold leading line-clamp-2 mt-12">
              {topic.title}
            </p>
            <div className="mt-12">
              <p className="text-base text-black-2 leading">{topic.body}</p>
            </div>
          </div>
          <div className="flex justify-center items-center mt-16">
            <button
              onClick={onClose}
              className="w-[128px] bg-black-4 py-12 px-24 text-base text-white leading font-bold rounded-full hover:opacity-80 mr-24"
            >
              キャンセル
            </button>
            <button
              onClick={onSubmit}
              className="w-[128px] bg-notification-1 py-12 px-24 text-base text-white leading font-bold rounded-full hover:opacity-80 mr-24"
            >
              通報する
            </button>
          </div>
        </div>
      </Modal>
      <LastConfirmModal
        opened={isLastConfirmModalOpened}
        onSubmit={onLastConfirmSubmit}
        onClose={onLastConfirmClose}
        isLoading={isReportLoading}
        isNotification={true}
      />
    </>
  );
};
